'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'

import {
  SettingsCard,
  SettingsCardHeader,
  SettingsRow,
  SettingsRowGroup,
} from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import EditCreditLimitsDialog from '@/features/settings/billing/credits/edit-credit-limits-dialog'
import { isSellableCredits } from '@/features/settings/billing/credits/lib/credits'
import type { ApiBillingView } from '@/features/settings/billing/credits/lib/mappers'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'

export default function CreditLimitsCard({ billing }: { billing: ApiBillingView }) {
  const t = useTranslations('settings.billing.credits.limits')
  const { credits, cost } = useCreditFormat()
  const [editOpen, setEditOpen] = useState(false)

  const { settings, creditPrice } = billing
  // Off sale, or the saved amount fell outside a since-narrowed range: the server skips refills.
  const refillSellable =
    creditPrice !== undefined && isSellableCredits(creditPrice, settings.refillCredits)

  return (
    <SettingsCard id="api-credit-limit">
      <div className="flex items-start justify-between gap-4">
        <SettingsCardHeader title={t('title')} description={t('description')} />
        <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
          {t('edit')}
        </Button>
      </div>
      <SettingsRowGroup>
        <SettingsRow
          label={t('limit')}
          control={
            <span className="text-foreground text-sm tabular-nums">
              {settings.spendLimitWeekly === undefined
                ? t('noLimit')
                : credits(settings.spendLimitWeekly)}
            </span>
          }
        />
        <SettingsRow
          label={t('refill')}
          description={settings.refillEnabled && !refillSellable ? t('refillPaused') : undefined}
          control={
            <span className="text-foreground text-right text-sm tabular-nums">
              {settings.refillEnabled
                ? t('refillOn', {
                    threshold: credits(settings.refillThreshold),
                    credits: refillSellable
                      ? `${credits(settings.refillCredits)} (${cost(creditPrice, settings.refillCredits)})`
                      : credits(settings.refillCredits),
                  })
                : t('refillOff')}
            </span>
          }
        />
      </SettingsRowGroup>

      <EditCreditLimitsDialog open={editOpen} onOpenChange={setEditOpen} />
    </SettingsCard>
  )
}
